import { MainArea, Modal, NotificationVariation, Page, WizardView } from '@salecycle/ui-component-library';
import AppTopBar from 'core/ui/components/AppTopBar/AppTopBar';
import { Actions as NotificationBarActions } from 'core/ui/notificationBar';
import { Campaign } from 'entities/campaign';
import { RulesetSplitTest } from 'entities/rulesetSplitTest';
import { Template } from 'entities/template';
import moment from 'moment';
import * as React from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { compose } from 'redux';
import { Actions as RouterActions, Selectors as RouterSelectors } from 'router/';
import { Selectors as TemplateSelectors } from 'sections/assets/state/templates';
import { Selectors as CampaignSelectors } from 'sections/campaigns/state/campaigns';
import {
  Actions as SplitTestActions,
  Selectors as SplitTestSelectors,
} from 'sections/campaigns/state/rulesetSplitTests';
import { State } from 'store/';
import { NewWizardSplitTestTemporaryId } from './constants';
import { validateSettings, validateVariantChoice } from './splitTestValidator';
import {
  createUrl,
  getPageIndex,
  getRelativePageRoute,
  SplitTestSettingsPage,
  SplitTestSummaryPage,
  SplitTestVariantChoicePage,
  wizardPages,
} from './wizardPages';

interface StateProps {
  otherSplitTestsForSite: RulesetSplitTest[];
  campaigns: Campaign[];
  siteId: string;
  step: string;
  splitTest?: RulesetSplitTest;
  templates: Template[];
  splitTestId: string;
  oldSplitTest?: RulesetSplitTest;
}

interface DispatchProps {
  push: (path: string) => void;
  createSplitTest: (id: string, splitTest: RulesetSplitTest) => void;
  create: (id: string, splitTest: RulesetSplitTest, params: {}) => void;
  update: (id: string, oldSplitTest: RulesetSplitTest, splitTest: RulesetSplitTest, params: {}) => void;
  discardChanges: () => void;
  showNotification: (variation: NotificationVariation, detail: string) => void;
}

interface IProps {
  t: any;
}
export type Props = StateProps & DispatchProps & IProps;

export const Wizard: React.FC<Props> = props => {
  const [modalVisible, setModalVisible] = React.useState(false);
  /* eslint-disable react-hooks/exhaustive-deps */
  React.useEffect(() => {
    if (!props.splitTest) {
      const newSplitTest: RulesetSplitTest = {
        name: '',
        type: 'Template',
        objective: 'ExtendReach',
        variants: [{ name: 'Variant A', weight: 50, type: '.TemplateVariant' }],
      };
      props.createSplitTest(NewWizardSplitTestTemporaryId, newSplitTest);
    }
  }, []);

  React.useEffect(() => {
    return props.discardChanges();
  }, []);

  if (!props.splitTest) {
    return null;
  }

  const startDate = moment(props.splitTest.startTime).format('Do MMMM YYYY');
  const endDate = moment(props.splitTest.endTime).format('Do MMMM YYYY');
  const onPrevButtonClicked = () => {
    const currentStep = getPageIndex(props.step);
    props.push(createUrl(props.siteId, props.splitTestId, getRelativePageRoute(currentStep - 1)));
  };

  const onNextButtonClicked = () => {
    if (props.step === 'summary') {
      showModal();
    } else {
      const currentStep = getPageIndex(props.step);
      props.push(createUrl(props.siteId, props.splitTestId, getRelativePageRoute(currentStep + 1)));
    }
  };

  const create = () =>
    props.create(NewWizardSplitTestTemporaryId, props.splitTest, {
      campaignId: props.splitTest.campaignId,
      rulesetId: props.splitTest.rulesetId,
    });

  const update = () =>
    props.update(props.splitTestId, props.oldSplitTest, props.splitTest, {
      campaignId: props.splitTest.campaignId,
      rulesetId: props.splitTest.rulesetId,
    });

  const isBackButtonDisabled = (): boolean => getPageIndex(props.step) === 0;
  const isNextButtonDisabled = (): boolean => {
    switch (getPageIndex(props.step)) {
      case 0:
        return !validateSettings(
          props.splitTest,
          props.campaigns.find(camp => camp.id === props.splitTest.campaignId),
          props.otherSplitTestsForSite,
        );
      case 1:
        return !validateVariantChoice(props.splitTest, props.templates);
      default:
        return false;
    }
  };

  const showModal = () => setModalVisible(true);
  const hideModal = () => setModalVisible(false);
  return (
    <MainArea>
      <AppTopBar />
      <Page padding={0}>
        <WizardView
          currentStep={getPageIndex(props.step) + 1}
          steps={wizardPages}
          padding={20}
          onNextButtonClicked={onNextButtonClicked}
          onPrevButtonClicked={onPrevButtonClicked}
          nextButtonDisabled={isNextButtonDisabled()}
          prevButtonDisabled={isBackButtonDisabled()}
          nextButtonLabel="Next"
        >
          <Switch>
            <Route path="/settings" component={SplitTestSettingsPage} />
            <Route path="/variant-choice" component={SplitTestVariantChoicePage} />
            <Route path="/summary" component={SplitTestSummaryPage} />
          </Switch>
        </WizardView>
        <Modal
          variation="clean"
          isOpen={modalVisible}
          heading="Split Test Confirmation"
          okButtonLabel={
            props.splitTestId === NewWizardSplitTestTemporaryId ? 'Confirm Split Test' : 'Update Split Test'
          }
          onOkButtonClicked={props.splitTestId === NewWizardSplitTestTemporaryId ? create : update}
          onCancelButtonClicked={hideModal}
        >
          <p>
            You&apos;re about to {props.splitTestId === NewWizardSplitTestTemporaryId ? 'create' : 'update'} split test{' '}
            <em>{props.splitTest.name || ''}</em> which will run from <em>{startDate}</em> to <em>{endDate}</em>.
          </p>
          <p>Select cancel to return to summary or confirm to complete.</p>
        </Modal>
      </Page>
    </MainArea>
  );
};

function mapStateToProps(state: State): StateProps {
  const splitTestId = RouterSelectors.getSplitTestId(state) || NewWizardSplitTestTemporaryId;

  return {
    otherSplitTestsForSite: SplitTestSelectors.otherSplitTests(state, splitTestId),
    campaigns: CampaignSelectors.getEntities(state),
    siteId: RouterSelectors.getSiteId(state),
    step: RouterSelectors.getSplitTestCreationWizardPage(state),
    templates: TemplateSelectors.getEntities(state),
    splitTest: SplitTestSelectors.getUpdatedEntity(state, splitTestId),
    splitTestId,
    oldSplitTest: SplitTestSelectors.getEntity(state, splitTestId),
  };
}

const Actions: DispatchProps = {
  push: RouterActions.push,
  createSplitTest: SplitTestActions.createLocalWithTemporaryId,
  create: SplitTestActions.requestCreate,
  update: SplitTestActions.updateRulesetSplitTest,
  discardChanges: SplitTestActions.discardAllLocal,
  showNotification: NotificationBarActions.customNotification,
};

export default compose(withTranslation(), connect(mapStateToProps, Actions))(Wizard);
